import { action, computed, makeObservable, runInAction } from 'mobx';

import { caldavApi, CalDavApiError } from '@/api/caldavApi';
import { yaMetrikaStore } from '@/entities/yaMetrika';
import { LoadingStageModel } from '@/models/LoadingStageModel';
import { ValueModel } from '@/models/ValueModel';
import { ValidatorResult } from '@/types/validator';
import { AnalyticsEvent } from '@/types/yametrika';

import { CALDAV_ALLOWED_EMAIL_DOMAIN, CALDAV_SERVER_URL } from '../config';
import {
  CalDavCalendar,
  CalDavConfigCreate,
  CalDavConfigResponse,
  CalDavStatusResponse,
  IntegrationStatus,
} from '../types';

const loginEmailValidator = (value: string): ValidatorResult => {
  if (!value) {
    return null;
  }

  if (!value.endsWith(CALDAV_ALLOWED_EMAIL_DOMAIN)) {
    return `Допускается только почта в домене ${CALDAV_ALLOWED_EMAIL_DOMAIN}`;
  }

  return null;
};

export class CalDavStore {
  private readonly _status = new ValueModel<CalDavStatusResponse | null>(null);
  private readonly _config = new ValueModel<CalDavConfigResponse | null>(null);
  private readonly _calendars = new ValueModel<CalDavCalendar[]>([]);
  private readonly _error = new ValueModel<string | null>(null);

  private readonly _statusLoading = new LoadingStageModel();
  private readonly _configLoading = new LoadingStageModel();
  private readonly _saveConfigLoading = new LoadingStageModel();
  private readonly _calendarsLoading = new LoadingStageModel();
  private readonly _syncLoading = new LoadingStageModel();
  private readonly _deleteLoading = new LoadingStageModel();

  // Form state
  private readonly _formLogin = new ValueModel<string>('', [loginEmailValidator]);
  private readonly _formPassword = new ValueModel<string>('');
  private readonly _formServerUrl = new ValueModel<string>(CALDAV_SERVER_URL);

  constructor() {
    makeObservable(this, {
      status: computed,
      integrationStatus: computed,
      config: computed,
      calendars: computed,
      error: computed,
      isStatusLoading: computed,
      isConfigLoading: computed,
      isSaveConfigLoading: computed,
      isCalendarsLoading: computed,
      isSyncLoading: computed,
      isDeleteLoading: computed,
      formLogin: computed,
      formLoginError: computed,
      formPassword: computed,
      formServerUrl: computed,
      hasUnsavedChanges: computed,
      fetchStatus: action.bound,
      fetchConfig: action.bound,
      saveConfig: action.bound,
      deleteConfig: action.bound,
      fetchCalendars: action.bound,
      syncCalendars: action.bound,
      updateCalendarSelection: action.bound,
      saveCalendarSelection: action.bound,
      prefillLogin: action.bound,
      setFormLogin: action.bound,
      setFormPassword: action.bound,
      resetForm: action.bound,
      clearError: action.bound,
    });
  }

  // Getters
  get status(): CalDavStatusResponse | null {
    return this._status.value;
  }

  get integrationStatus(): IntegrationStatus {
    return this._status.value?.status ?? 'not_configured';
  }

  get config(): CalDavConfigResponse | null {
    return this._config.value;
  }

  get calendars(): CalDavCalendar[] {
    return this._calendars.value;
  }

  get error(): string | null {
    return this._error.value;
  }

  get isStatusLoading(): boolean {
    return this._statusLoading.isLoading;
  }

  get isConfigLoading(): boolean {
    return this._configLoading.isLoading;
  }

  get isSaveConfigLoading(): boolean {
    return this._saveConfigLoading.isLoading;
  }

  get isCalendarsLoading(): boolean {
    return this._calendarsLoading.isLoading;
  }

  get isSyncLoading(): boolean {
    return this._syncLoading.isLoading;
  }

  get isDeleteLoading(): boolean {
    return this._deleteLoading.isLoading;
  }

  get formLogin(): string {
    return this._formLogin.value;
  }

  get formLoginError(): string | null {
    return this._formLogin.error;
  }

  get formPassword(): string {
    return this._formPassword.value;
  }

  get formServerUrl(): string {
    return this._formServerUrl.value;
  }

  get hasUnsavedChanges(): boolean {
    // Check if calendar selection changed
    return this._calendars.touched;
  }

  // Actions
  async fetchStatus(): Promise<void> {
    this._statusLoading.loading();

    try {
      const status = await caldavApi.getStatus();
      runInAction(() => {
        this._status.change(status);
        this._statusLoading.success();
      });
    } catch (error) {
      runInAction(() => {
        this._statusLoading.error();
      });

      throw error;
    }
  }

  async fetchConfig(): Promise<void> {
    this._configLoading.loading();

    try {
      const config = await caldavApi.getConfig();
      runInAction(() => {
        this._config.change(config);

        if (config) {
          this._formLogin.change(config.login, true);
          this._formServerUrl.change(config.serverUrl, true);
        }

        this._configLoading.success();
      });
    } catch (error) {
      runInAction(() => {
        this._configLoading.error();
      });

      throw error;
    }
  }

  async saveConfig(): Promise<void> {
    if (this._formLogin.validate()) {
      return;
    }

    this._saveConfigLoading.loading();
    this._error.change(null);

    const data: CalDavConfigCreate = {
      serverUrl: this._formServerUrl.value,
      login: this._formLogin.value,
      password: this._formPassword.value,
    };

    try {
      const config = await caldavApi.saveConfig(data);
      runInAction(() => {
        this._config.change(config);
        this._formPassword.change('', true);
        this._saveConfigLoading.success();
      });
      await this.fetchStatus();
      yaMetrikaStore.sendEvent(AnalyticsEvent.caldavConnect);
    } catch (error) {
      runInAction(() => {
        if (error instanceof CalDavApiError) {
          this._error.change(error.message);
        } else {
          this._error.change('Произошла ошибка при сохранении настроек');
        }

        this._saveConfigLoading.error();
      });

      throw error;
    }
  }

  async deleteConfig(): Promise<void> {
    this._deleteLoading.loading();

    try {
      await caldavApi.deleteConfig();
      runInAction(() => {
        this._config.change(null);
        this._calendars.change([]);
        this._status.change(null);
        this.resetForm();
        this._deleteLoading.success();
      });
      await this.fetchStatus();
      yaMetrikaStore.sendEvent(AnalyticsEvent.caldavDisconnect);
    } catch (error) {
      runInAction(() => {
        this._deleteLoading.error();
      });

      throw error;
    }
  }

  async fetchCalendars(): Promise<void> {
    this._calendarsLoading.loading();

    try {
      const calendars = await caldavApi.getCalendars();
      runInAction(() => {
        this._calendars.change(calendars, true);
        this._calendarsLoading.success();
      });
    } catch (error) {
      runInAction(() => {
        this._calendarsLoading.error();
      });

      throw error;
    }
  }

  async syncCalendars(): Promise<void> {
    this._syncLoading.loading();
    this._error.change(null);

    try {
      const calendars = await caldavApi.syncCalendars();
      runInAction(() => {
        this._calendars.change(calendars, true);
        this._syncLoading.success();
      });
      await this.fetchStatus();
      yaMetrikaStore.sendEvent(AnalyticsEvent.caldavSync);
    } catch (error) {
      runInAction(() => {
        if (error instanceof CalDavApiError) {
          this._error.change(error.message);
        } else {
          this._error.change('Произошла ошибка при синхронизации календарей');
        }

        this._syncLoading.error();
      });

      throw error;
    }
  }

  updateCalendarSelection(calendarId: string, isSelected: boolean): void {
    const calendars = this._calendars.value.map((cal) =>
      cal.id === calendarId ? { ...cal, isSelected } : cal,
    );
    this._calendars.change(calendars);
  }

  async saveCalendarSelection(): Promise<void> {
    this._calendarsLoading.loading();

    try {
      const updates = this._calendars.value.map((cal) => ({
        id: cal.id,
        isSelected: cal.isSelected,
      }));
      const calendars = await caldavApi.updateCalendars({ calendars: updates });
      runInAction(() => {
        this._calendars.change(calendars, true);
        this._calendarsLoading.success();
      });
      await this.fetchStatus();
    } catch (error) {
      runInAction(() => {
        this._calendarsLoading.error();
      });

      throw error;
    }
  }

  prefillLogin(email: string): void {
    if (!this._config.value && !this._formLogin.value) {
      this._formLogin.change(email, true);
    }
  }

  setFormLogin(value: string): void {
    this._formLogin.change(value);
  }

  setFormPassword(value: string): void {
    this._formPassword.change(value);
  }

  resetForm(): void {
    this._formLogin.reset();
    this._formPassword.reset();
    this._formServerUrl.change(CALDAV_SERVER_URL, true);
    this._error.change(null);
  }

  clearError(): void {
    this._error.change(null);
  }

  destroy(): void {
    // Cleanup if needed
  }
}
